import styled from "styled-components";
import { Grid, Flex, Stack, Skeleton } from "@mantine/core";
import { CardStatus } from "../../components/Card/CardStatus";
import { CardContainerRetro } from "../../components/Container/CardContainerRetro";
import { Title } from "@mantine/core";
import colorsCustom from "../../designTokens/colorsCustom";
import { useTranslation } from "react-i18next";
import other from "../../designTokens/other.esm";
import React, { useEffect, useState } from "react";
import { DataLoadError } from "../../components/error/DataLoadError";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useTheme } from "@mui/material/styles";
import errorLogger from "../../utils/errorLogger";

// 트랜지션이 적용된 컨테이너
const AnimatedSection = styled(Flex)`
	transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const StableCardContainer = styled(CardContainerRetro)`
	transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
	min-height: auto;
`;

// 메트릭 데이터용 애니메이션 컨테이너
const AnimatedMetricContainer = styled(Flex)`
	transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
	opacity: ${(props) => (props.isLoading ? 0.8 : 1)};
	transform: scale(${(props) => (props.isLoading ? 0.99 : 1)});
`;

const Col = styled(Grid.Col)`
	border-right: ${(props) =>
		`solid ${props.$isEnd ? "0" : "0.5px"} ${colorsCustom.ui.dark.subtle}`};
	transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

export const KeyMetricSummarySection = ({
	year,
	month,
	views,
	profileViews,
	monthlyViews,
	likes,
	replies,
	reposts,
	postLoading,
	loading,
	dataError,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();

	// 반응형 Hook
	const isXL = useIsMobile("xl");
	const isSM = useIsMobile("sm");

	// 안전한 데이터 처리 - 에러가 있어도 기본값 제공
	const [safeDisplayData, setSafeDisplayData] = useState({
		profileViews: 0,
		views: 0,
		likes: 0,
		replies: 0,
		reposts: 0,
		monthlyViews: [],
	});

	// displayData는 safeDisplayData로 통합됨

	// 안전한 데이터 처리 - 무한 루프 방지
	useEffect(() => {
		// 데이터 유효성 검사
		const isValidData =
			profileViews !== undefined &&
			!isNaN(profileViews) &&
			Array.isArray(monthlyViews);

		if (!postLoading && isValidData) {
			const newData = {
				profileViews: Number(profileViews) || 0,
				views: Number(views) || 0,
				likes: Number(likes) || 0,
				replies: Number(replies) || 0,
				reposts: Number(reposts) || 0,
				monthlyViews: Array.isArray(monthlyViews) ? monthlyViews : [],
			};

			// 데이터가 실제로 변경된 경우에만 상태 업데이트
			setSafeDisplayData((prevData) => {
				if (
					prevData.profileViews !== newData.profileViews ||
					prevData.views !== newData.views ||
					prevData.likes !== newData.likes ||
					prevData.replies !== newData.replies ||
					prevData.reposts !== newData.reposts ||
					prevData.monthlyViews.length !== newData.monthlyViews.length
				) {
					return newData;
				}
				return prevData;
			});
		} else if (dataError) {
			// 에러 상태: 기본값 사용
			setSafeDisplayData((prevData) => {
				if (prevData.profileViews !== 0) {
					return {
						profileViews: 0,
						views: 0,
						likes: 0,
						replies: 0,
						reposts: 0,
						monthlyViews: [],
					};
				}
				return prevData;
			});
		}
		// postLoading이 true일 때는 상태를 변경하지 않음 (기존 데이터 유지)
	}, [
		profileViews,
		views,
		likes,
		replies,
		reposts,
		monthlyViews,
		postLoading,
		dataError,
	]);

	// 로딩 skeleton 렌더링 함수
	const renderLoadingSkeleton = () => {
		return (
			<AnimatedMetricContainer
				w={"100%"}
				p={isXL ? "16px" : "24px"}
				gap={24}
				direction={"column"}
				isLoading={true}
			>
				<Grid
					direction={"row"}
					w={"100%"}
					gutter={isSM ? 0 : 8}
					p={"0px"}
					style={{
						display: "flex",
						alignItems: "stretch",
						minHeight: isSM ? "auto" : "109px",
					}}
				>
					<Col span={isSM ? 12 : 4.9} $isEnd={isSM}>
						<Flex direction="column" gap={8} h="100%">
							<Skeleton height={16} width="60%" />
							<Skeleton height={24} width="80%" />
							{!isSM && <Skeleton height={40} width="100%" />}
						</Flex>
					</Col>
					{!isSM && (
						<>
							<Col span={1.8} pl={16}>
								<Flex direction="column" gap={8} h="100%">
									<Skeleton height={12} width="100%" />
									<Skeleton height={20} width="70%" />
								</Flex>
							</Col>
							<Col span={"auto"} pl={16}>
								<Flex direction="column" gap={8} h="100%">
									<Skeleton height={12} width="100%" />
									<Skeleton height={20} width="70%" />
								</Flex>
							</Col>
							<Col span={"auto"} pl={16}>
								<Flex direction="column" gap={8} h="100%">
									<Skeleton height={12} width="100%" />
									<Skeleton height={20} width="70%" />
								</Flex>
							</Col>
							<Col span={"auto"} $isEnd pl={16}>
								<Flex direction="column" gap={8} h="100%">
									<Skeleton height={12} width="100%" />
									<Skeleton height={20} width="70%" />
								</Flex>
							</Col>
						</>
					)}
				</Grid>
			</AnimatedMetricContainer>
		);
	};

	// 데이터 에러가 있을 때 경고 표시가 포함된 UI
	const renderMetricWithErrorWarning = () => {
		if (dataError && safeDisplayData.profileViews === 0) {
			try {
				errorLogger.log({
					component: "KeyMetricSummarySection",
					event: "monthly_insight_failed",
					endpoint: "threads_insights",
					status: dataError?.response?.status,
					errorCode: dataError?.code,
					tokenPresent:
						!!localStorage.getItem("token") ||
						!!localStorage.getItem("access_token"),
					// month/year는 상위에서 전달된 프롭으로 이미 존재. 안전상 undefined 허용
					month,
					year,
					extra: { profileViews: 0 },
				});
			} catch (e) {}
			return (
				<AnimatedMetricContainer
					w={"100%"}
					p={isXL ? "16px" : "24px"}
					gap={16}
					direction={"column"}
					isLoading={postLoading}
				>
					<DataLoadError
						title={t("error.performanceDataFailed")}
						message={t("error.threadsDataLoadFailed")}
						showRetry={false}
					/>
				</AnimatedMetricContainer>
			);
		}

		return (
			<AnimatedMetricContainer
				w={"100%"}
				p={isXL ? "16px" : "24px"}
				gap={24}
				direction={"column"}
				isLoading={postLoading}
			>
				{dataError && safeDisplayData.profileViews > 0 && (
					<Flex
						align="center"
						gap={8}
						p={8}
						style={{
							backgroundColor: "#fff3cd",
							borderRadius: 8,
							border: "1px solid #ffeaa7",
						}}
					>
						<span style={{ color: "#856404", fontSize: "12px" }}>
							⚠️ {t("error.partialDataWarning")}
						</span>
					</Flex>
				)}

				<Grid
					direction={"row"}
					w={"100%"}
					gutter={isSM ? 0 : 8}
					p={"0px"}
					style={{
						display: "flex",
						alignItems: "stretch",
						minHeight: isSM ? "auto" : "109px",
					}}
				>
					<Col span={isSM ? 12 : 4.9} $isEnd={isSM}>
						<CardStatus
							type={"profileVisit"}
							isMobile={isSM}
							value={safeDisplayData.profileViews}
							monthlyViews={safeDisplayData.monthlyViews}
							postLoading={postLoading}
						/>
					</Col>
					{!isSM && (
						<>
							<Col span={1.8} pl={16}>
								<CardStatus
									type={"view"}
									value={safeDisplayData.views}
									postLoading={postLoading}
								/>
							</Col>
							<Col span={"auto"} pl={16}>
								<CardStatus
									type={"like"}
									value={safeDisplayData.likes}
									postLoading={postLoading}
								/>
							</Col>
							<Col span={"auto"} pl={16}>
								<CardStatus
									type={"reply"}
									value={safeDisplayData.replies}
									postLoading={postLoading}
								/>
							</Col>
							<Col span={"auto"} $isEnd pl={16}>
								<CardStatus
									type={"repost"}
									value={safeDisplayData.reposts}
									postLoading={postLoading}
								/>
							</Col>
						</>
					)}
				</Grid>
			</AnimatedMetricContainer>
		);
	};

	return (
		<AnimatedSection
			direction={"column"}
			w={
				isXL
					? "calc(100% - 32px)"
					: isSM
					? "calc(100% - 32px)"
					: `calc(${other.size.lg} + 24px)`
			}
			pt={32}
			pb={isXL ? 0 : 64}
			pl={isXL ? 0 : 0}
			style={{
				position: "fixed",
				top: 64,
				left: isXL
					? 16
					: isSM
					? 16
					: `calc((100% - ${other.size.lg} + 302px)/2 - 16px)`,
				right: isXL ? 16 : isSM ? 16 : "auto",
				zIndex: 90,
				background: `linear-gradient(to bottom, 
          ${theme.palette.background.default}00 0px,
          ${theme.palette.background.default}80 16px,
          ${theme.palette.background.default} 32px,
          ${theme.palette.background.default} 248px,
          ${theme.palette.background.default}00 280px)`,
			}}
		>
			<Stack gap={isXL ? 8 : 16} w={"100%"}>
				<Title order={isXL ? 5 : 3} fw={500} mt={8}>
					{t("dashboard.monthlyMetric", { month, year })}
				</Title>
				<StableCardContainer
					bg={colorsCustom.paper}
					w={"100%"}
					weight="bold"
					rd={16}
					$isSM={isSM}
				>
					{postLoading && safeDisplayData.profileViews === 0
						? renderLoadingSkeleton()
						: renderMetricWithErrorWarning()}
				</StableCardContainer>
			</Stack>
		</AnimatedSection>
	);
};
